import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { EvChipItem, EvComboboxOption } from '@evinced-private/ui-common';

import { useGetTenantUsers } from 'src/hooks/queries/useGetTenantUsers';
import { getActiveTenantUsers } from 'src/services/TenantsService';

import { SchedulingSectionContext } from './context';
import {
	cacheEmailRecipientLabel,
	cacheEmailRecipientLabels,
	getCachedEmailRecipientLabel,
	getEmailRecipientLabelCacheSnapshot
} from './EmailRecipientLabelCache';
import {
	DEFAULT_PAGE_SIZE,
	RECIPIENT_LOOKUP_PAGE_SIZE,
	SEARCH_PAGE_SIZE
} from './EmailSettings.constants';
import {
	getInactiveEmailRecipientLabel,
	getUserLabel,
	mapUsersToOptions
} from './EmailSettings.utils';

interface UseEmailRecipientsReturn {
	recipients: EvChipItem[];
	initialOptions: EvComboboxOption[];
	loadUserOptions: (searchText: string, cb: (options: EvComboboxOption[]) => void) => void;
	handleRecipientSelect: (selected: EvComboboxOption[]) => void;
	handleRemoveRecipient: (chipId: string) => void;
}

export const useEmailRecipients = (): UseEmailRecipientsReturn => {
	const { config, updateConfig } = useContext(SchedulingSectionContext);
	const { data: tenantUsers } = useGetTenantUsers(undefined, RECIPIENT_LOOKUP_PAGE_SIZE);
	const [cachedLabelById] = useState(() => {
		return getEmailRecipientLabelCacheSnapshot();
	});
	const activeTenantUsers = useMemo(
		() => tenantUsers?.filter((user) => user.active !== false) ?? [],
		[tenantUsers]
	);
	const userLabelById = useMemo(() => {
		const labels = new Map(cachedLabelById);
		(tenantUsers ?? []).forEach((user) => {
			const userLabel = getUserLabel(user);
			labels.set(
				user.id,
				user.active === false ? getInactiveEmailRecipientLabel(userLabel) : userLabel
			);
		});
		return labels;
	}, [cachedLabelById, tenantUsers]);
	const [recipients, setRecipients] = useState<EvChipItem[]>(() =>
		(config.emailRecipientUserIds || []).map((userId) => ({
			id: userId,
			label: userLabelById.get(userId) || getCachedEmailRecipientLabel(userId) || userId
		}))
	);

	useEffect(() => {
		if (!tenantUsers?.length) {
			return;
		}

		const labelsById = new Map<string, string>();
		tenantUsers.forEach((user) => {
			const userLabel = getUserLabel(user);
			labelsById.set(
				user.id,
				user.active === false ? getInactiveEmailRecipientLabel(userLabel) : userLabel
			);
		});
		cacheEmailRecipientLabels(labelsById);
	}, [tenantUsers]);

	useEffect(() => {
		const recipientIds = config.emailRecipientUserIds || [];
		const cachedRecipientLabelById = getEmailRecipientLabelCacheSnapshot();
		setRecipients((prevRecipients) => {
			const previousRecipientById = new Map(
				prevRecipients.map((recipient) => [recipient.id, recipient])
			);
			const nextRecipients = recipientIds.map((userId) => ({
				id: userId,
				label:
					userLabelById.get(userId) ||
					cachedRecipientLabelById.get(userId) ||
					previousRecipientById.get(userId)?.label ||
					userId
			}));
			const unchanged =
				nextRecipients.length === prevRecipients.length &&
				nextRecipients.every(
					(recipient, index) =>
						recipient.id === prevRecipients[index]?.id &&
						recipient.label === prevRecipients[index]?.label
				);

			return unchanged ? prevRecipients : nextRecipients;
		});
	}, [config.emailRecipientUserIds, userLabelById]);

	const disabledIds = useMemo(() => new Set(recipients.map((r) => r.id)), [recipients]);

	const initialOptions = useMemo(
		() => mapUsersToOptions(activeTenantUsers, disabledIds),
		[activeTenantUsers, disabledIds]
	);

	const recipientsWithResolvedLabels = useMemo(
		() =>
			recipients.map((recipient) => ({
				...recipient,
				label: userLabelById.get(recipient.id) || recipient.label
			})),
		[recipients, userLabelById]
	);

	const loadUserOptions = useCallback(
		(searchText: string, cb: (options: EvComboboxOption[]) => void): void => {
			const pageSize = searchText ? SEARCH_PAGE_SIZE : DEFAULT_PAGE_SIZE;
			getActiveTenantUsers(searchText, pageSize).then((users) => {
				cb(mapUsersToOptions(users, disabledIds));
			});
		},
		[disabledIds]
	);

	const handleRecipientSelect = useCallback(
		(selected: EvComboboxOption[]): void => {
			if (!selected?.[0]) {
				return;
			}
			const option = selected[0];
			const userId = String(option.value);
			if (recipients.some((r) => r.id === userId)) {
				return;
			}

			cacheEmailRecipientLabel(userId, option.label);
			const nextRecipients = [...recipients, { id: userId, label: option.label }];
			setRecipients(nextRecipients);
			updateConfig({ emailRecipientUserIds: nextRecipients.map((r) => r.id) });
		},
		[recipients, updateConfig]
	);

	const handleRemoveRecipient = useCallback(
		(chipId: string): void => {
			const nextRecipients = recipients.filter((r) => r.id !== chipId);
			if (nextRecipients.length === recipients.length) {
				return;
			}

			setRecipients(nextRecipients);
			updateConfig({ emailRecipientUserIds: nextRecipients.map((r) => r.id) });
		},
		[recipients, updateConfig]
	);

	return {
		recipients: recipientsWithResolvedLabels,
		initialOptions,
		loadUserOptions,
		handleRecipientSelect,
		handleRemoveRecipient
	};
};
